<script setup lang="ts">
// One actionable row. Emits `select` on click / Enter / Space (Reka). The menu
// auto-closes after select unless the handler calls `event.preventDefault()`
// (use that for in-place toggles like a theme switch). `as-child` lets the row
// BE another element - e.g. a RouterLink - while keeping the menuitem role.
import { DropdownMenuItem } from 'reka-ui';

defineProps<{
  disabled?: boolean | undefined;
  danger?: boolean | undefined;
  asChild?: boolean | undefined;
}>();
defineEmits<{ (e: 'select', event: Event): void }>();
</script>

<template>
  <DropdownMenuItem
    class="hq-menu__item"
    :class="{ 'hq-menu__item--danger': danger }"
    :disabled="disabled"
    :as-child="asChild"
    @select="$emit('select', $event)"
  >
    <slot />
  </DropdownMenuItem>
</template>

<!-- Unscoped: portalled to <body> by Reka (scoped hashes don't reach it). -->
<style>
.hq-menu__item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: 0;
  border-radius: var(--radius-input);
  background: transparent;
  color: var(--ink-primary);
  font: inherit;
  font-size: var(--text-sm);
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  outline: none;
  transition: background var(--dur-1) var(--ease-out), color var(--dur-1) var(--ease-out);
}
/* Reka sets data-highlighted on the keyboard/pointer-focused row. */
.hq-menu__item[data-highlighted],
.hq-menu__item:hover { background: var(--surface-sunken); }
.hq-menu__item[data-disabled] { opacity: 0.5; cursor: default; pointer-events: none; }
.hq-menu__item svg { flex-shrink: 0; color: var(--ink-muted); }

.hq-menu__item--danger { color: var(--status-danger); }
.hq-menu__item--danger svg { color: var(--status-danger); }
.hq-menu__item--danger[data-highlighted],
.hq-menu__item--danger:hover { background: var(--status-danger-soft); }
</style>
